/* =============================================================================
   VIEWS / ADMIN / SHELL — Hybrid-Navigation (Topbar-Gruppen + Sub-Tab-Bar)
   =============================================================================
   v0.30.0 / Session 20 / Phase 16.1
   ----------------------------------------------------------------------------
   Ersetzt die alte flache .admin-tabs-Bar durch eine zweistufige Navigation:
     1. Topbar mit 5 thematischen Gruppen (Heute / Operativ / Ware / Auswertung /
        System). „Plan & Karte" (Tischplan + Speisekarte) ist seit S167 KEIN
        eigener Tab mehr, sondern Untergruppe von System (Jonas 28.06.).
     2. Persistente Sub-Tab-Bar darunter, die die Tabs der aktiven Gruppe zeigt.

   API:
     renderAdminShell()             — rendert/aktualisiert Topbar + Sub-Tabs.
     groupForTab(tabId)             — Lookup-Helper.
     getActiveGroup() / getActiveTab() — State-Getter.

   Integration:
     - mountAdminShell() wird einmal aus bootstrap.js (bzw. showAdminPanel)
       aufgerufen und legt das DOM-Gerüst neben dem alten .admin-tabs an.
     - switchTab() (view-switch.js) ruft updateShellActiveTab() — bleibt
       kompatibel mit allen alten data-tab-Buttons im DOM.
============================================================================ */

import {
  effectiveRole, visibleGroupIds, canSeeOperatorPresence, restrictedTabIds,
  effectiveRoles, visibleGroupIdsForRoles, restrictedTabIdsForRoles, ROLE_LABELS
} from '../../core/operator-roles.js';
import { htmlEscape } from '../../utils/helpers.js';

/** Definition der 5 Hybrid-Gruppen. Reihenfolge im Array = Reihenfolge in der UI. */
export const ADMIN_GROUPS = [
  {
    id: 'today',
    label: 'Heute',
    icon: 'sun',
    tabs: [
      { id: 'dashboard', label: 'Dashboard' }
    ]
  },
  {
    id: 'operations',
    label: 'Operativ',
    icon: 'flame',
    tabs: [
      { id: 'orders',    label: 'Bestellungen', badgeRef: 'orders-tab-counter' },
      // S106/S108 — Aufräumung der Operativ-Subtab-Leiste:
      //  • 'reservations' (S106 Variante a → S108): Leisten-Button entfernt; mit
      //    S108 ist die Listen-Sicht komplett abgeschafft (kein #tab-reservations,
      //    kein Routing, kein Deep-Link mehr). Reservierungen werden ausschließlich
      //    über das Dashboard verwaltet (Karten + „Als Nächstes" →
      //    openReservationDetail-Modal).
      //  • 'reminders': der manuelle Erinnerungs-Pfad ist komplett raus —
      //    Erinnerungen laufen jetzt ausschließlich automatisch (Cron
      //    enqueue_reservation_reminders, idempotent via reminder_enqueued_at).
      //  • 'kds'/'bar'/'delivery': aus der Leiste raus, bleiben über die
      //    Topbar-Pills (renderTopbarHtml, oben rechts) + Dashboard erreichbar.
      { id: 'occupancy', label: 'Belegung' }
    ]
  },
  {
    // S263 (Jonas 2026-09-14) — eigener Reiter „Dienstplan" zwischen Operativ und
    // Auswertung (Spec § 6.2): die Manager-Maske (Wochenraster 12–16 · 16–22 ·
    // 12–22). Sicht manager+ (VIEW_MATRIX), Service/Staff/Fahrer sehen ihn nicht —
    // die eigenen Schichten kommen per Mail/PDF. Im Team-Tab bleiben nur
    // Teilnahme-Schalter + Zyklus-Karte. Hash #admin/schedule/plan.
    id: 'schedule',
    label: 'Dienstplan',
    icon: 'calendar',
    tabs: [
      { id: 'plan', label: 'Wochenplan' }
    ]
  },
  {
    // S189 (Jonas 2026-07-01) — „Auswertung" steht jetzt VOR „Ware" (Reihenfolge
    // getauscht): Kennzahlen sind der häufigere Einstieg als die ERP-Pflege.
    id: 'reports',
    label: 'Auswertung',
    icon: 'chart',
    tabs: [
      { id: 'day-close', label: 'Tagesabschluss' },
      { id: 'stats',     label: 'Statistik' },
      { id: 'archive',   label: 'Archiv' }
    ]
  },
  {
    // S157 — eigene Top-Level-Gruppe „Ware" (Warenwirtschaft/ERP). Sammelt die
    // flag-gateten ERP-Subtabs (vorher fälschlich unter „Plan & Karte").
    id: 'ware',
    label: 'Ware',
    icon: 'box',
    tabs: [
      // ERP nur bei Feature-Flag (additiv, wie bisher). E1: + Lager.
      // S188-U — Studio zog unter System → Speisekarte. S189 — „Food-Cost" ist kein
      // eigener Tab mehr: der Report lebt jetzt in Auswertung → Statistik →
      // Warenwirtschaft (dort integriert). „Ware" = reine ERP-Pflege.
      ...((typeof window !== 'undefined' && window.AppConfig && window.AppConfig.features && window.AppConfig.features.ingredientsDb)
        ? [{ id: 'ingredients', label: 'Zutaten' }, { id: 'lager', label: 'Lager' }, { id: 'suppliers', label: 'Lieferanten' }, { id: 'purchase-orders', label: 'Bestellungen' }, { id: 'shrinkage', label: 'Schwund' }, { id: 'lots', label: 'Chargen' }] : [])
    ],
    // S203 Cut A / HOTFIX A2 — echte zweite Navigationsebene im Ware-Bereich
    // (Jonas-Review: die v1-Inline-Cluster brachten keinen Übersichts-Gewinn).
    // Drei Obertabs (Sections) in der Ware-Leiste; Bestand/Einkauf klappen eine
    // Untertab-Zeile darunter auf, „Zutaten" ist ein direkter Tab (eine tabId →
    // keine Untertab-Zeile). `tabs` bleibt die Single-Source für groupForTab/
    // Routing/Rollen-Filter — `sections` verweist nur auf bestehende tab.id's.
    // Reihenfolge Sections = Anzeige-Reihenfolge; Reihenfolge tabIds = Untertab-
    // Reihenfolge. Rollen-Filter greift über `tabs` (rollen-gefiltert) → leere
    // Sections/Untertabs verschwinden sauber (Lager-Rolle: kein Zutaten/Lieferanten).
    sections: [
      { id: 'ingredients', label: 'Zutaten', tabIds: ['ingredients'] },
      { id: 'bestand',     label: 'Bestand', tabIds: ['lager', 'lots', 'shrinkage'] },
      { id: 'einkauf',     label: 'Einkauf', tabIds: ['purchase-orders', 'suppliers'] }
    ]
  },
  {
    id: 'system',
    label: 'System',
    icon: 'cog',
    tabs: [
      { id: 'settings', label: 'Einstellungen' },
      // S167 (Jonas 28.06.) — „Plan & Karte" ist kein eigener Top-Level-Tab mehr:
      // Tischplan + Speisekarte sind Untertabs unter System → schlankere Topbar,
      // mehr Platz. Eigene Render-/Permission-Pfade (NICHT settings-readonly-
      // gewrappt) bleiben unverändert → Manager behalten Schreibrechte.
      { id: 'layout', label: 'Tischplan' },
      { id: 'menu',   label: 'Speisekarte' },
      // S169 P2 — Deals-Verwaltung (eigener Render-/Permission-Pfad wie Menü,
      // owner/admin/manager). Schreibt direkt via RLS, kein settings-readonly-Wrap.
      { id: 'deals',  label: 'Deals' },
      // S179 Teil B / R2 — Rabatt-Verwaltung (prozentual, ohne Fenster).
      // Gleicher Render-/Permission-Pfad wie Deals (owner/admin/manager, RLS).
      { id: 'discounts', label: 'Rabatte' }
    ]
  }
];

// S108 — `ROUTABLE_HIDDEN_TABS` entfernt: die einzige Nutzung war `reservations`
// (S106 Variante a). Mit der Abschaffung der Reservierungs-Listen-Sicht ist
// `reservations` kein routebarer/deep-linkbarer Tab mehr — `groupForTab` und
// `readStateFromHash` brauchen keine Sonder-Map mehr.

/** Inline-SVG-Icons im Lucide-Look (stroke-current). */
const ICONS = {
  sun:    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/></svg>',
  flame:  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"><path d="M8.5 14.5A2.5 2.5 0 0 0 11 17c1.5 0 2.8-.6 3.5-1.7"/><path d="M16 17c0 2.5-2 4-4 4s-4-1.5-4-4c0-1.6.5-2.2 2-3.5C12 11.5 11 10.5 13 8c2 2 5 5 5 9z"/></svg>',
  layout: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></svg>',
  box:    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>',
  chart:  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3v18h18"/><path d="M7 14l3-3 3 3 4-5"/></svg>',
  // S263 — Dienstplan-Reiter.
  calendar: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><path d="M8 14h3M13 14h3M8 18h3"/></svg>',
  cog:    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h.01a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v.01a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>',
  logout: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>',
  search: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>'
};

/** State: Welche Gruppe ist aktiv? Welcher Tab? */
const _state = { activeGroup: 'today', activeTab: 'dashboard' };

/** Lookup: zu welcher Gruppe gehört ein Tab? */
export function groupForTab(tabId) {
  for (const g of ADMIN_GROUPS) {
    if (g.tabs.some(t => t.id === tabId)) return g.id;
  }
  return 'today';
}

/**
 * S131 (Phase D): View-Gating nach der **Operator**-Rolle (nicht mehr nach dem
 * Auth-/Geräte-Konto). Liefert nur die Gruppen, die der aktive Operator sehen
 * darf (VIEW_MATRIX in core/operator-roles.js). OHNE angemeldeten Operator gilt
 * die restriktivste Sicht „staff" (Dashboard) — Aktionen lösen dann das
 * PIN-Overlay aus (server-fest via assert_operator_role).
 *
 * Fallback (Legacy/Tests): ist der Operator-Layer gar nicht geladen
 * (`window.App.operator` fehlt), wird der volle Satz angezeigt — dann greift
 * ausschließlich die server-seitige Durchsetzung.
 */
export function getVisibleAdminGroups() {
  const opApi = (typeof window !== 'undefined' && window.App && window.App.operator) ? window.App.operator : null;
  if (!opApi || typeof opApi.getOperator !== 'function') {
    // Operator-Layer nicht verfügbar -> ungegated (Server bleibt autoritativ).
    // S157 — Leer-Gruppen-Filter AUCH hier, sonst zeigt der Fallback bei Flag aus
    // eine leere „Ware"-Pille (gilt robust für jede künftige flag-gatete Gruppe).
    return ADMIN_GROUPS
      .map(g => ({ ...g, tabs: g.tabs.slice() }))
      .filter(g => g.tabs.length > 0);
  }
  // S167 Multi-Role: Sichtbarkeit = UNION über alle Rollen des Operators.
  const roles = effectiveRoles(opApi.getOperator());
  const allowed = new Set(visibleGroupIdsForRoles(roles));
  return ADMIN_GROUPS
    .filter(g => allowed.has(g.id))
    .map(g => {
      // S167 — Sub-Tab-Einschränkung je Rolle (z.B. „Lager" sieht im Ware-Tab
      // nur Lager/Bestellungen/Schwund/Chargen). null = keine Einschränkung
      // (mind. eine Rolle sieht die Gruppe voll).
      const only = restrictedTabIdsForRoles(roles, g.id);
      const tabs = only ? g.tabs.filter(t => only.includes(t.id)) : g.tabs.slice();
      return { ...g, tabs };
    })
    .filter(g => g.tabs.length > 0);
}

/** Ist der Tab für die aktive Rolle sichtbar? (Gruppe UND Sub-Tab-Restriktion). */
export function isAdminTabVisible(tabId) {
  return getVisibleAdminGroups().some(g => g.tabs.some(t => t.id === tabId));
}

/** Erster für die aktive Rolle sichtbare Tab (Fallback-Ziel des Routen-Guards
 *  für Rollen ohne „today", z.B. „Lager"). Default 'dashboard'. */
export function firstVisibleAdminTab() {
  const g = getVisibleAdminGroups();
  return (g[0] && g[0].tabs[0]) ? g[0].tabs[0].id : 'dashboard';
}

/** Aktive Operator-Rolle fürs Gating (effektiv, „staff" wenn keiner aktiv). */
export function activeOperatorRole() {
  const opApi = (typeof window !== 'undefined' && window.App && window.App.operator) ? window.App.operator : null;
  const op = (opApi && typeof opApi.getOperator === 'function') ? opApi.getOperator() : null;
  return effectiveRole(op);
}

export function getActiveGroup() { return _state.activeGroup; }
export function getActiveTab()   { return _state.activeTab; }

/**
 * S168 — Operator-Chip in der Topbar (oben rechts). IMMER sichtbar, unabhängig
 * von Tab-Rechten → der Operator-Wechsel/-Abmelden ist nie „eingesperrt" (Befund
 * Lager-Operator, der nur „Ware" sah). Öffnet dasselbe Wechsel-Modal wie die
 * Dashboard-Kachel (operatorOpenSwitch). Operator wird über die window.App-Bridge
 * gelesen (kein harter Import — degradet sauber, wenn der Layer fehlt).
 */
function renderOperatorChipHtml() {
  const opApi = (typeof window !== 'undefined' && window.App && window.App.operator) ? window.App.operator : null;
  const op = (opApi && typeof opApi.getOperator === 'function') ? opApi.getOperator() : null;
  if (op && op.name) {
    const initials = op.initials || fallbackInitials(op.name);
    const role = ROLE_LABELS[op.role] || op.role || '';
    const title = `${op.name}${role ? ' · ' + role : ''} — Operator wechseln / abmelden`;
    return `
      <button type="button" class="pill op-switch-pill" onclick="operatorOpenSwitch()" title="${htmlEscape(title)}">
        <span class="op-switch-badge" aria-hidden="true">${htmlEscape(initials)}</span>
        <span class="label">${htmlEscape(op.name)}</span>
      </button>
    `;
  }
  return `
    <button type="button" class="pill op-switch-pill op-switch-empty" onclick="operatorOpenSwitch()" title="Operator anmelden">
      <span aria-hidden="true">👤</span><span class="label">Operator</span>
    </button>
  `;
}

/** Rendert die Topbar (Gruppen-Buttons) — eigene HTML-Generation.
 *  Phase 41 (S50): rendert nur Gruppen mit mindestens einem sichtbaren Tab. */
function renderTopbarHtml() {
  const visibleGroups = getVisibleAdminGroups();
  // S132e — „Wer ist aktiv" als Initialen-Chips in der Topbar (ersetzt die
  // Dashboard-Section). Nur manager/owner/admin; Inhalt wird async/realtime über
  // ensureActiveOperatorsWidget() in #active-operators-chips gefüllt.
  const showChips = canSeeOperatorPresence(activeOperatorRole());
  const chipsHtml = showChips
    ? '<span id="active-operators-chips" class="op-chips" aria-label="Aktive Operatoren"></span>'
    : '';
  const grpButtons = visibleGroups.map(g => `
    <button type="button"
            data-group="${g.id}"
            onclick="adminShellSwitchGroup('${g.id}')"
            class="${g.id === _state.activeGroup ? 'active' : ''}"
            aria-label="${g.label}">
      <span class="grp-icon" aria-hidden="true">${ICONS[g.icon] || ''}</span>
      <span class="grp-label">${g.label}</span>
    </button>
  `).join('');

  return `
    <div class="admin-shell-topbar">
      <nav class="admin-group-bar" role="tablist" aria-label="Bereiche">
        ${grpButtons}
      </nav>
      <div class="admin-shell-tools">
        ${chipsHtml}
        ${renderOperatorChipHtml()}
        <button type="button" class="pill danger" onclick="confirmAccountLogout()"
                title="Vom Benutzerkonto abmelden — NICHT der Operator (dafür der Chip daneben)">
          <span aria-hidden="true">${ICONS.logout}</span><span class="label">Konto abmelden</span>
        </button>
      </div>
    </div>
  `;
}

/** Ein einzelner Subtab-Button (S203 Cut A extrahiert — identisches Markup wie
 *  zuvor, damit Cluster- und Flach-Rendering denselben Button teilen). */
function _renderSubtabButton(t) {
  // KDS + Bar sind externe Vollbild-Views, keine Tabs — eigener Click-Handler.
  if (t.external) {
    // Mapping: tab.id → View-Name in switchView (kds → 'kds', bar → 'bar')
    const viewName = t.id;
    return `
        <button type="button"
                onclick="switchView('${viewName}')"
                title="${t.label} als Vollbild-View">
          ${t.label} <span style="color: var(--ink-muted); font-size: .75rem;">↗</span>
        </button>
      `;
  }
  const badgeHtml = t.badgeRef
    ? `<span class="sub-badge" id="${t.badgeRef}" style="display:none;">0</span>`
    : '';
  const active = t.id === _state.activeTab ? 'active' : '';
  return `
      <button type="button"
              data-subtab="${t.id}"
              onclick="switchTab('${t.id}')"
              class="${active}"
              role="tab" aria-selected="${active ? 'true' : 'false'}">
        <span>${t.label}</span>${badgeHtml}
      </button>
    `;
}

// S203 HOTFIX A2 — zuletzt aktiver Untertab je Ware-Section (Merken der Wahl,
// nice-to-have). Modul-State (überlebt Re-Render); kein View-Hijack.
const _wareSectionLastTab = {};

/** Rollen-gefilterte Sections einer Gruppe: jede Section mit ihren sichtbaren Tabs
 *  (Reihenfolge = section.tabIds), leere Sections weggefiltert. */
function _visibleSections(group) {
  if (!Array.isArray(group.sections)) return [];
  const byId = new Map(group.tabs.map(t => [t.id, t]));  // group.tabs ist rollen-gefiltert
  return group.sections
    .map(sec => ({ ...sec, tabs: (sec.tabIds || []).map(id => byId.get(id)).filter(Boolean) }))
    .filter(sec => sec.tabs.length > 0);
}

/** Rendert die Sub-Tab-Bar für die aktuell aktive Gruppe.
 *  Phase 41 (S50): nutzt die rollen-gefilterte Gruppen-Liste.
 *  S203 HOTFIX A2: hat die Gruppe `sections` (Ware), wird eine zweistufige
 *  Navigation gerendert — Section-Zeile (Obertabs) + darunter die Untertab-Zeile
 *  der aktiven Section (nur wenn ≥ 2 sichtbare Tabs; „Zutaten" = 1 Tab = direkt). */
function renderSubtabsHtml() {
  const visibleGroups = getVisibleAdminGroups();
  const group = visibleGroups.find(g => g.id === _state.activeGroup) || visibleGroups[0];
  if (!group || group.tabs.length === 0) return '<div class="admin-subtabs"></div>';

  if (Array.isArray(group.sections)) {
    const sections = _visibleSections(group);
    if (!sections.length) return '<div class="admin-subtabs"></div>';
    // Aktive Section = die mit dem aktiven Tab (sonst erste). Wahl merken.
    let active = sections.find(s => s.tabs.some(t => t.id === _state.activeTab)) || sections[0];
    if (active.tabs.some(t => t.id === _state.activeTab)) _wareSectionLastTab[active.id] = _state.activeTab;

    const sectionBtns = sections.map(sec => {
      const on = sec.id === active.id;
      // Ziel-Tab beim Klick: zuletzt aktiver Untertab der Section, sonst erster sichtbarer.
      const remembered = _wareSectionLastTab[sec.id];
      const target = (remembered && sec.tabs.some(t => t.id === remembered)) ? remembered : sec.tabs[0].id;
      return `
        <button type="button" data-ware-section="${sec.id}"
                onclick="switchTab('${target}')"
                class="${on ? 'active' : ''}" role="tab" aria-selected="${on ? 'true' : 'false'}">
          <span>${htmlEscape(sec.label)}</span>
        </button>`;
    }).join('');
    const sectionRow = `<div class="admin-subtabs admin-ware-sections" role="tablist" aria-label="Ware-Bereiche">${sectionBtns}</div>`;

    // Untertab-Zeile nur, wenn die aktive Section mehr als einen sichtbaren Tab hat
    // („Zutaten" mit einem Tab öffnet direkt, keine Untertab-Zeile).
    const subRow = active.tabs.length >= 2
      ? `<div class="admin-subtabs admin-ware-subtabs" role="tablist" aria-label="${htmlEscape(active.label)} Sub-Tabs">${active.tabs.map(_renderSubtabButton).join('')}</div>`
      : '';
    return sectionRow + subRow;
  }

  const inner = group.tabs.map(_renderSubtabButton).join('');
  return `<div class="admin-subtabs" role="tablist" aria-label="${group.label} Sub-Tabs">${inner}</div>`;
}

/** Mount: legt das Shell-DOM neben den alten .admin-tabs an, blendet sie aus. */
export function mountAdminShell() {
  const panel = document.getElementById('admin-panel');
  if (!panel) return;
  const oldTabs = panel.querySelector('.admin-tabs');
  if (!oldTabs) return;
  // Doppelten Mount verhindern
  if (panel.querySelector('.admin-shell')) {
    renderAdminShell();
    return;
  }
  // Vor der ersten .container .admin-tabs den Shell-Container hängen
  const shell = document.createElement('div');
  shell.className = 'admin-shell';
  shell.innerHTML = renderTopbarHtml() + renderSubtabsHtml();
  // Wir setzen den Shell-Container DIREKT in das .container-Element vor .admin-tabs
  oldTabs.parentNode.insertBefore(shell, oldTabs);
  panel.classList.add('has-hybrid-nav');
  // Den orders-tab-counter aus der alten Topbar in die neue Sub-Bar migrieren —
  // wir entfernen die Duplikat-ID dort, damit nur ein Element gestyled wird.
  const oldCounter = oldTabs.querySelector('#orders-tab-counter');
  if (oldCounter) oldCounter.id = 'orders-tab-counter-legacy';

  // S56 Block 0.3 — Cockpit-Brand-Footer "by YOUR GROWTH 365"
  // Idempotent, an admin-panel angehängt damit über allen Tabs sichtbar.
  if (!panel.querySelector('.admin-shell-footer')) {
    const footer = document.createElement('div');
    footer.className = 'admin-shell-footer';
    const version = (window.AppConfig && window.AppConfig.appVersion) || '';
    footer.innerHTML = `Cockpit by <strong>YOUR GROWTH 365</strong>` +
      (version ? ` · <span class="admin-shell-footer-version">${version}</span>` : '');
    panel.appendChild(footer);
  }
}

/** Voll-Re-Render der Topbar + Sub-Tabs (nach Gruppen-/Tab-Wechsel). */
export function renderAdminShell() {
  const shell = document.querySelector('#admin-panel .admin-shell');
  if (!shell) {
    mountAdminShell();
    return;
  }
  shell.innerHTML = renderTopbarHtml() + renderSubtabsHtml();
  // Counter nach Re-Render neu setzen — der span#orders-tab-counter
  // wurde durch das innerHTML-Reset frisch erstellt und steht auf display:none.
  if (typeof window.refreshOrdersTabCounter === 'function') {
    window.refreshOrdersTabCounter();
  }
  // S132e — Topbar-Presence-Chips füllen + Realtime abonnieren (nur sichtbare Rolle).
  // Der Container #active-operators-chips wurde durch das innerHTML-Reset neu erzeugt.
  try {
    if (canSeeOperatorPresence(activeOperatorRole())) ensureActiveOperatorsWidget();
  } catch (e) { console.warn('[shell] presence chips:', e && e.message); }
}

/** Update der Active-States, ohne komplettes Re-Render. */
function updateActiveStates() {
  const shell = document.querySelector('#admin-panel .admin-shell');
  if (!shell) return;
  shell.querySelectorAll('[data-group]').forEach(b => {
    b.classList.toggle('active', b.dataset.group === _state.activeGroup);
  });
  shell.querySelectorAll('[data-subtab]').forEach(b => {
    const on = b.dataset.subtab === _state.activeTab;
    b.classList.toggle('active', on);
    b.setAttribute('aria-selected', on ? 'true' : 'false');
  });
}

/**
 * Gruppe wechseln. Aktiviert automatisch den ersten Tab der Gruppe — außer
 * der bereits aktive Tab gehört schon zur Ziel-Gruppe.
 * Phase 41 (S50): Nutzt rollen-gefilterte Gruppen. Gruppen ohne sichtbare
 * Tabs sind nicht ansteuerbar.
 */
export function switchGroup(groupId) {
  const visibleGroups = getVisibleAdminGroups();
  const group = visibleGroups.find(g => g.id === groupId);
  if (!group) return;
  _state.activeGroup = groupId;
  // Wenn der aktive Tab nicht in der neuen Gruppe ist, ersten Tab nehmen
  const tabInGroup = group.tabs.find(t => t.id === _state.activeTab && !t.external);
  if (!tabInGroup) {
    const first = group.tabs.find(t => !t.external);
    if (first) {
      _state.activeTab = first.id;
      // Bestehende switchTab-Logik aufrufen, damit Tab-Section gerendert wird
      if (typeof window.switchTab === 'function') {
        window.switchTab(first.id);
      }
    }
  }
  renderAdminShell();
  syncHash();
}

/**
 * Wird aus switchTab (view-switch.js) aufgerufen, NACH dem die Tab-Section
 * gerendert wurde. Setzt die Gruppe passend zum Tab und updated den Shell.
 */
export function updateShellActiveTab(tabId) {
  _state.activeTab = tabId;
  const grp = groupForTab(tabId);
  // Wenn der Tab in einer anderen Gruppe liegt, automatisch Gruppe wechseln
  if (grp !== _state.activeGroup) {
    _state.activeGroup = grp;
    renderAdminShell();
  } else if (grp === 'ware') {
    // S203 HOTFIX A2 — zweistufige Ware-Nav: Section-Zeile + Untertab-Zeile hängen
    // vom aktiven Tab ab (Section-Wechsel tauscht die Untertab-Zeile) → Voll-Re-
    // Render statt bloßem Klassen-Toggle.
    renderAdminShell();
  } else {
    updateActiveStates();
  }
  syncHash();
}

/** Hash-Sync: #admin/<group>/<tab>. Schluckt KDS-Hash + Cancel-Hash. */
function syncHash() {
  if (typeof window === 'undefined') return;
  // KDS-View hat eigene Hash-Logik
  if (window.location.hash.includes('#kds')) return;
  const newHash = '#admin/' + _state.activeGroup + '/' + _state.activeTab;
  if (window.location.hash !== newHash) {
    // History-Update ohne neuen Eintrag (Back-Button bleibt sinnvoll)
    try {
      history.replaceState(null, '', window.location.pathname + window.location.search + newHash);
    } catch (_) { /* ignore */ }
  }
}

/** Reading-State aus dem URL-Hash. Backward-Compat: #admin und #admin/<tab> alt. */
export function readStateFromHash() {
  const hash = (typeof window !== 'undefined' && window.location.hash) || '';
  // S188-U — Legacy-Redirect: das Studio zog von „Ware → Rubriken" (categories)
  // unter „System → Speisekarte" (menu-Hub, Default-Ansicht Studio). Alte
  // Bookmarks/Deep-Links (#admin/ware/categories, #admin/categories) sauber
  // umbiegen, statt auf einer toten Route zu landen.
  if (/(^#admin\/[^/]+\/categories\b)|(^#admin\/categories\b)/.test(hash)) {
    return { group: 'system', tab: 'menu' };
  }
  // S189 — Food-Cost zog von „Ware" nach Auswertung → Statistik (Warenwirtschaft).
  if (/(^#admin\/[^/]+\/food-cost\b)|(^#admin\/food-cost\b)/.test(hash)) {
    return { group: 'reports', tab: 'stats' };
  }
  // Pattern: #admin/<group>/<tab>
  const m = hash.match(/^#admin\/([^/]+)\/([^/]+)/);
  if (m) {
    const grpId = m[1];
    const tabId = m[2];
    // S157 — Tab muss zur GENANNTEN Gruppe gehören.
    const grp = ADMIN_GROUPS.find(g => g.id === grpId);
    if (grp && grp.tabs.some(t => t.id === tabId)) {
      return { group: grpId, tab: tabId };
    }
    // Alter Deep-Link (z. B. #admin/planning/ingredients, jetzt unter „ware"):
    // über den TAB die korrekte Gruppe auflösen (Tab muss dort real existieren).
    const realGrp = groupForTab(tabId);
    if (ADMIN_GROUPS.some(g => g.id === realGrp && g.tabs.some(t => t.id === tabId))) {
      return { group: realGrp, tab: tabId };
    }
  }
  // Legacy-Pattern: #admin/<tab>
  const m2 = hash.match(/^#admin\/([^/]+)/);
  if (m2) {
    const tabId = m2[1];
    const grpId = groupForTab(tabId);
    if (grpId) return { group: grpId, tab: tabId };
  }
  return null;
}

/** Tastatur-Shortcut: G+<key> wechselt zur Gruppe. */
function bindKeyboardShortcuts() {
  // S167 — 'p' (planning) entfällt: Tischplan/Speisekarte sind jetzt unter System (G+S).
  // S263 — 'd' = Dienstplan (G+D).
  const map = { h: 'today', o: 'operations', d: 'schedule', w: 'ware', a: 'reports', s: 'system' };
  let buffering = false;
  let bufferTimer = null;
  document.addEventListener('keydown', (e) => {
    // Nicht in Inputs feuern
    const tag = (e.target && e.target.tagName) || '';
    if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tag) || (e.target && e.target.isContentEditable)) return;
    if (e.metaKey || e.ctrlKey || e.altKey) return;

    if (buffering) {
      const key = e.key.toLowerCase();
      if (map[key]) {
        e.preventDefault();
        switchGroup(map[key]);
      }
      buffering = false;
      clearTimeout(bufferTimer);
      return;
    }
    if (e.key.toLowerCase() === 'g') {
      buffering = true;
      bufferTimer = setTimeout(() => { buffering = false; }, 800);
    }
  });
}

// S125 — Operator-Layer (Geräte-Onboarding, Operator-State, Idle-Sperre) wird
// einmalig beim Admin-Mount initialisiert. Idempotent (ensureOperatorLayer self-guard).
import { ensureOperatorLayer, ensureActiveOperatorsWidget, fallbackInitials } from './operator-ui.js';
// S140 — MainUser-Auto-Logout bei Inaktivität (deckelt persistSession zusätzlich
// zum geräte-gated Operator-Idle).
import { startMainIdleWatch, stopMainIdleWatch, clearLastActivity, clampTimeoutMinutes } from '../../core/session-idle.js';
import { operatorLogout } from '../../core/operator-session.js';

/** Timeout-Quelle: globaler Settings-Wert (Default 180), geclampt 15–720 → ms. */
function _mainIdleTimeoutMs() {
  let raw;
  try {
    const st = window.App && window.App.storage && window.App.storage._getInternalState();
    raw = st && st.settings && st.settings.mainUserIdleTimeoutMinutes;
  } catch (_) { /* ignore */ }
  return clampTimeoutMinutes(raw) * 60 * 1000;
}

/** Idle-Logout: Operator best-effort zuerst (JWT kann nach Idle abgelaufen sein,
 *  Memory feedback_gg_jwt_refresh_idle), danach der harte Cut signOut(). */
async function _onMainIdleLogout() {
  try { await operatorLogout('idle'); } catch (_) { /* best-effort */ }
  try { if (window.App && window.App.auth) await window.App.auth.logout(); } catch (_) { /* signOut ist der harte Cut */ }
}

/** Einmalige Verdrahtung des MainUser-Idle-Watchers + Logout-Cleanup. */
function _ensureMainIdleWatch() {
  try {
    startMainIdleWatch({ getTimeoutMs: _mainIdleTimeoutMs, onLogout: _onMainIdleLogout });
  } catch (e) { console.warn('[shell] startMainIdleWatch:', e && e.message); }
  // Beim Logout (idle ODER manuell) Watcher stoppen + Stempel löschen, sonst
  // würde der nächste frische Login per Boot-Check sofort wieder rausfliegen.
  if (window.App && window.App.events && typeof window.App.events.on === 'function'
      && !window._mainIdleLogoutSub) {
    window._mainIdleLogoutSub = true;
    window.App.events.on('auth:logout', () => {
      stopMainIdleWatch();
      clearLastActivity();
    });
  }
}

/** Boot: nach showAdminPanel aufrufen. Idempotent. */
export function initAdminShell() {
  mountAdminShell();
  // S125: Operator-Layer hochfahren (fire-and-forget, self-guarded).
  try { ensureOperatorLayer(); } catch (e) { console.warn('[shell] ensureOperatorLayer:', e && e.message); }
  // S140: MainUser-Idle-Watcher (Boot-Check + Poll), neben dem Operator-Layer.
  _ensureMainIdleWatch();
  // Hash-State (falls Deep-Link) übernehmen
  const fromHash = readStateFromHash();
  if (fromHash) {
    _state.activeGroup = fromHash.group;
    _state.activeTab   = fromHash.tab;
  }
  renderAdminShell();
  // Listen auf hashchange für Deep-Link-Navigation
  if (!window._adminShellHashListener) {
    window._adminShellHashListener = true;
    window.addEventListener('hashchange', () => {
      const s = readStateFromHash();
      if (s && (s.group !== _state.activeGroup || s.tab !== _state.activeTab)) {
        // S107a — KEIN `_state.activeGroup = s.group` hier: das pre-set ließ
        // `updateShellActiveTab` (via switchTab) den Gruppen-Wechsel verpassen
        // (`grp === _state.activeGroup` → nur .active-Toggle statt renderAdminShell)
        // → die Subtab-Leiste blieb bei Deep-Link-/Hash-Sprüngen auf der alten
        // Gruppe stehen. updateShellActiveTab setzt die Gruppe jetzt selbst + baut
        // die Leiste korrekt neu.
        if (typeof window.switchTab === 'function') {
          window.switchTab(s.tab);
        }
      }
    });
    bindKeyboardShortcuts();
  }

  // S131 (Phase D): Operator-Wechsel schaltet die Navigation live um. Liegt die
  // gerade aktive View für die neue Rolle nicht mehr frei → genau EIN
  // kontrollierter Redirect aufs Dashboard (bewusste No-View-Hijack-Ausnahme).
  if (window.App && window.App.events && typeof window.App.events.on === 'function'
      && !window._operatorShellRoleSub) {
    window._operatorShellRoleSub = true;
    window.App.events.on('operator:changed', () => {
      try {
        renderAdminShell();
        // switchTab() trägt den Routen-Guard: liegt die aktive View für die neue
        // Rolle nicht frei → Redirect aufs Dashboard, sonst Re-Render der aktiven
        // View unter der neuen Rolle (Dashboard flippt staff↔voll, Settings
        // read-only↔editierbar). Genau EIN kontrollierter Umschalter pro Wechsel.
        if (typeof window.switchTab === 'function') window.switchTab(_state.activeTab);
      } catch (e) { console.warn('[shell] operator:changed:', e && e.message); }
    });
  }
}

// Backward-Compat: globaler Zugriff für inline-onclick + view-switch.js
if (typeof window !== 'undefined') {
  Object.assign(window, {
    initAdminShell,
    renderAdminShell,
    mountAdminShell,
    updateShellActiveTab,
    adminShellSwitchGroup: switchGroup,
    adminShellOpenKds: () => window.switchView && window.switchView('kds'),
    groupForTab,
    activeOperatorRole,
    getVisibleAdminGroups,
    isAdminTabVisible,
    firstVisibleAdminTab,
    ADMIN_GROUPS
  });
}
